import { useId } from 'react'
import { useTheme } from '@mui/material/styles'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'

export type GradientFaIconProps = {
  icon: IconDefinition
  /** Two stops (0% → 100%) or three stops (0% → ~53% → 100%). Defaults to labraAIGradient. */
  colors?: string[]
  className?: string
}

/**
 * Renders a Font Awesome icon with a linear gradient fill. A CSS `background-clip:
 * text` gradient does not paint on an SVG glyph, so the gradient is applied as an
 * SVG `<linearGradient>` referenced by the path `fill`.
 */
export const GradientFaIcon = ({
  icon,
  colors,
  className,
}: GradientFaIconProps) => {
  const theme = useTheme()
  const stops = colors ?? [
    theme.palette.labraAIGradient.start,
    theme.palette.labraAIGradient.middle,
    theme.palette.labraAIGradient.end,
  ]
  const iconData = icon.icon
  const gradientId = useId()
  if (!iconData) return null
  const [w, h, , , d] = iconData

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          {stops.length >= 3 ? (
            <>
              <stop offset="0%" stopColor={stops[0]} />
              <stop offset="53%" stopColor={stops[1]} />
              <stop offset="100%" stopColor={stops[2] ?? stops[1]} />
            </>
          ) : (
            <>
              <stop offset="0%" stopColor={stops[0]} />
              <stop offset="100%" stopColor={stops[1] ?? stops[0]} />
            </>
          )}
        </linearGradient>
      </defs>
      {Array.isArray(d) ? (
        d.map(pathData => (
          <path key={pathData} d={pathData} fill={`url(#${gradientId})`} />
        ))
      ) : (
        <path d={d as string} fill={`url(#${gradientId})`} />
      )}
    </svg>
  )
}
